import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import CommonModal from "../../../components/common/CommonModal";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useHouse, House } from "../../../hooks/useHouse";
import { useError } from "../../../contexts/ErrorContext";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import HouseFormModal from "../../../components/Modals/HouseFormModal";

const HouseManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const {
    getAllHouseQuery,
    addHouseMutation,
    updateHouseMutation,
    deleteHouseMutation,
  } = useHouse();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentHouse, setCurrentHouse] = useState<House | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [houseToDelete, setHouseToDelete] = useState<number | null>(null);

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentHouse(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (house: House) => {
    setIsEdit(true);
    setCurrentHouse(house);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentHouse(undefined);
  };

  const handleSubmit = (formData: House) => {
    if (isEdit && formData.id != null) {
      updateHouseMutation.mutate(formData, {
        onSuccess: () => {
          showSuccess(t("house_updated_successfully") || "House updated successfully");
          handleCloseModal();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          showError(
            err?.response?.data?.message ||
              err?.message ||
              t("error_updating_house") ||
              "Error updating house"
          );
        },
      });
    } else {
      addHouseMutation.mutate(formData, {
        onSuccess: () => {
          showSuccess(t("house_added_successfully") || "House added successfully");
          handleCloseModal();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          showError(
            err?.response?.data?.message ||
              err?.message ||
              t("error_adding_house") ||
              "Error adding house"
          );
        },
      });
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setHouseToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setHouseToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (houseToDelete == null) return;
    deleteHouseMutation.mutate(houseToDelete, {
      onSuccess: () => {
        showSuccess(t("house_deleted_successfully") || "House deleted successfully");
        handleCloseDeleteDialog();
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        showError(
          err?.response?.data?.message ||
            err?.message ||
            t("error_deleting_house") ||
            "Error deleting house"
        );
      },
    });
  };

  const columns = [
    { id: "houseCode", label: t("house_code") || "House Code", minWidth: 120 },
    { id: "houseName", label: t("house_name") || "House Name", minWidth: 150 },
    {
      id: "houseColor",
      label: t("house_color") || "House Color",
      minWidth: 100,
      align: "center" as const,
      format: (value: string) => {
        const color = value && value !== "-" ? value : "#e0e0e0";
        return (
          <Box
            sx={{
              width: 40,
              height: 28,
              borderRadius: 1,
              backgroundColor: color,
              border: "1px solid rgba(0, 0, 0, 0.15)",
              mx: "auto",
            }}
          />
        );
      },
    },
  ];

  const formattedData = (getAllHouseQuery.data || []).map((row) => ({
    ...row,
    houseName: row.houseName || "-",
    houseCode: row.houseCode || "-",
    houseColor: row.houseColor || "",
  }));

  const filteredHouseData = React.useMemo(() => {
    return formattedData.filter((row) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase().trim();
      return (
        String(row.houseName || "").toLowerCase().includes(searchLower) ||
        String(row.houseCode || "").toLowerCase().includes(searchLower) ||
        String(row.houseColor || "").toLowerCase().includes(searchLower)
      );
    });
  }, [formattedData, searchTerm]);

  const isSubmitting = addHouseMutation.isLoading || updateHouseMutation.isLoading;

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: "1.25rem",
              whiteSpace: "nowrap",
            }}
          >
            {t("house_management") || "House Management"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField size="small" placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
                height: 36 
              }}
              onClick={handleOpenAddModal}
            >
              {t("add_house") || "Add House"}
            </Button>
          </Box>
        </Box>

        {getAllHouseQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : getAllHouseQuery.error ? (
          <Alert severity="error" sx={{ mb: 1, py: 0.5, px: 1.5 }}>
            {getAllHouseQuery.error.message || t("error_loading_houses") || "Error loading houses"}
          </Alert>
        ) : (
          <EnhancedTable
            columns={columns}
            data={filteredHouseData}
            searchFields={[]}
            filterFields={[]}
            actions={(row: House) => (
              <ActionButtons
                onEdit={() => handleOpenEditModal(row)}
                onDelete={() => row.id != null && handleOpenDeleteDialog(row.id)}
                editTooltip={t("edit")}
                deleteTooltip={t("delete")}
              />
            )}
            cardProps={{
              elevation: 0,
              sx: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 0,
                '& .MuiCardContent-root': {
                  p: 0,
                  '&:last-child': { pb: 0 }
                }
              },
            }}
          />
        )}
      </Paper>

      <HouseFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        initialData={currentHouse}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        title={t("confirm_delete")}
        maxWidth="sm"
        onSubmit={handleConfirmDelete}
        submitLabel={t("delete")}
        submitDisabled={deleteHouseMutation.isLoading}
        danger
      >
        <Typography variant="body1" color="text.secondary">
          {t("are_you_sure_you_want_to_delete_this_house") ||
            "Are you sure you want to delete this house?"}
        </Typography>
      </CommonModal>
    </>
  );
};

export default HouseManagement;



